import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  LayoutDashboard, Users, Building2, DollarSign, BarChart3, LogOut,
  Target, Gift, ChevronDown, ChevronRight, Globe, UserCircle, Link2,
  Activity, MapPin, CreditCard, FlaskConical, Menu, X, Search,
  Bell, AlertTriangle, UserPlus, Clock, Shield, Wallet, Megaphone, Zap, MessageSquare, Briefcase, Award
} from 'lucide-react';
import { supabase } from '../supabase';

const sections = [
  {
    id: 'platform',
    label: 'Platform',
    defaultOpen: true,
    items: [
      { id: 'overview', label: 'Overview', icon: Globe },
      { id: 'analytics', label: 'Analytics', icon: Activity },
      { id: 'companies', label: 'Companies', icon: Building2 },
      { id: 'users', label: 'Users', icon: Users },
      { id: 'local-resources', label: 'Local Resources', icon: MapPin },
    ]
  },
  {
    id: 'affiliates',
    label: 'Affiliates',
    defaultOpen: false,
    items: [
      { id: 'affiliates', label: 'Affiliates', icon: UserCircle },
      { id: 'accounts', label: 'Attribution', icon: Link2 },
      { id: 'leads', label: 'Lead Tracker', icon: Target },
      { id: 'customer-referrals', label: 'Customer Referrals', icon: Gift },
      { id: 'messages', label: 'Messages', icon: MessageSquare },
      { id: 'announcements', label: 'Announcements', icon: Megaphone },
      { id: 'spifs', label: 'SPIFs', icon: Zap },
      { id: 'recruitment', label: 'Recruitment', icon: Briefcase },
    ]
  },
  {
    id: 'finance',
    label: 'Finance',
    defaultOpen: false,
    items: [
      { id: 'commissions', label: 'Commissions', icon: DollarSign },
      { id: 'payouts', label: 'Payouts', icon: Wallet },
      { id: 'seu', label: 'SEU Grants', icon: Award },
      { id: 'reports', label: 'Reports', icon: BarChart3 },
      { id: 'stripe-reconcile', label: 'Stripe', icon: CreditCard },
    ]
  },
  {
    id: 'tools',
    label: 'Tools',
    defaultOpen: false,
    items: [
      { id: 'test-accounts', label: 'Test Accounts', icon: FlaskConical },
      { id: 'audit-log', label: 'Audit Log', icon: Shield },
    ]
  }
];

function GlobalSearch({ onSearchNavigate, isMobile }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ companies: [], users: [], affiliates: [] });
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Ctrl+K / Cmd+K shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setShowDropdown(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const performSearch = useCallback(async (searchTerm) => {
    if (searchTerm.length < 2) {
      setResults({ companies: [], users: [], affiliates: [] });
      setShowDropdown(false);
      return;
    }
    setLoading(true);
    try {
      const pattern = `%${searchTerm}%`;
      const [companiesRes, usersRes, affiliatesRes] = await Promise.all([
        supabase.from('companies').select('id, name').ilike('name', pattern).limit(5),
        supabase.from('user_profiles').select('id, full_name, email').or(`full_name.ilike.${pattern},email.ilike.${pattern}`).limit(5),
        supabase.from('affiliates').select('id, name').ilike('name', pattern).limit(5)
      ]);
      const newResults = {
        companies: companiesRes.data || [],
        users: usersRes.data || [],
        affiliates: affiliatesRes.data || []
      };
      setResults(newResults);
      const hasResults = newResults.companies.length > 0 || newResults.users.length > 0 || newResults.affiliates.length > 0;
      setShowDropdown(hasResults);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => performSearch(val), 300);
  };

  const handleSelect = (type, item) => {
    setShowDropdown(false);
    setQuery('');
    setResults({ companies: [], users: [], affiliates: [] });
    if (onSearchNavigate) onSearchNavigate(type, item);
  };

  const dropdownItemStyle = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    background: 'transparent',
    border: 'none',
    color: '#e0e0e0',
    fontSize: '0.85rem',
    cursor: 'pointer',
    textAlign: 'left',
    display: 'block'
  };

  const groupLabelStyle = {
    padding: '0.4rem 0.75rem 0.2rem',
    fontSize: '0.7rem',
    fontWeight: '600',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <Search
          size={15}
          style={{
            position: 'absolute',
            left: '0.6rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#888',
            pointerEvents: 'none'
          }}
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => {
            if (query.length >= 2) {
              const hasResults = results.companies.length > 0 || results.users.length > 0 || results.affiliates.length > 0;
              if (hasResults) setShowDropdown(true);
            }
          }}
          placeholder={isMobile ? 'Search...' : 'Search... (Ctrl+K)'}
          style={{
            width: '100%',
            padding: '0.5rem 0.6rem 0.5rem 2rem',
            background: '#242424',
            border: '1px solid #2a2a2a',
            borderRadius: '6px',
            color: '#e0e0e0',
            fontSize: '0.85rem',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />
      </div>
      {showDropdown && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          background: '#1e1e1e',
          border: '1px solid #2a2a2a',
          borderRadius: '8px',
          zIndex: 100,
          maxHeight: '320px',
          overflowY: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
        }}>
          {results.companies.length > 0 && (
            <div>
              <div style={groupLabelStyle}>Companies</div>
              {results.companies.map(c => (
                <button
                  key={c.id}
                  onClick={() => handleSelect('company', c)}
                  style={dropdownItemStyle}
                  onMouseEnter={e => e.currentTarget.style.background = '#2a2a2a'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <Building2 size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle', color: '#ff6b35' }} />
                  {c.name}
                </button>
              ))}
            </div>
          )}
          {results.users.length > 0 && (
            <div>
              <div style={groupLabelStyle}>Users</div>
              {results.users.map(u => (
                <button
                  key={u.id}
                  onClick={() => handleSelect('user', u)}
                  style={dropdownItemStyle}
                  onMouseEnter={e => e.currentTarget.style.background = '#2a2a2a'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <Users size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle', color: '#3b82f6' }} />
                  {u.full_name || u.email}
                  {u.full_name && <span style={{ color: '#666', marginLeft: '0.5rem', fontSize: '0.8rem' }}>{u.email}</span>}
                </button>
              ))}
            </div>
          )}
          {results.affiliates.length > 0 && (
            <div>
              <div style={groupLabelStyle}>Affiliates</div>
              {results.affiliates.map(a => (
                <button
                  key={a.id}
                  onClick={() => handleSelect('affiliate', a)}
                  style={dropdownItemStyle}
                  onMouseEnter={e => e.currentTarget.style.background = '#2a2a2a'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <UserCircle size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle', color: '#22c55e' }} />
                  {a.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationCenter({ onNavigate }) {
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [lastChecked, setLastChecked] = useState(() => {
    return localStorage.getItem('wv_notif_last_checked') || '2000-01-01T00:00:00Z';
  });
  const containerRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;

      const [expiredRes, signupsRes, commissionsRes] = await Promise.all([
        // Expired trials
        supabase
          .from('companies')
          .select('id, name, trial_end, subscription_status')
          .eq('subscription_status', 'trialing')
          .lt('trial_end', now.toISOString())
          .order('trial_end', { ascending: false })
          .limit(20),
        // Recent signups (last 7 days)
        supabase
          .from('companies')
          .select('id, name, created_at')
          .gte('created_at', sevenDaysAgo)
          .order('created_at', { ascending: false })
          .limit(20),
        // Check if current month has commissions calculated
        supabase
          .from('commissions')
          .select('id', { count: 'exact', head: true })
          .eq('period_month', currentMonth)
      ]);

      const items = [];

      // Expired trials
      if (expiredRes.data) {
        expiredRes.data.forEach(c => {
          items.push({
            id: `expired-${c.id}`,
            type: 'expired_trial',
            icon: AlertTriangle,
            iconColor: '#ef4444',
            title: 'Expired trial',
            description: c.name,
            timestamp: c.trial_end,
            action: () => onNavigate && onNavigate('company', { id: c.id, name: c.name })
          });
        });
      }

      // Recent signups
      if (signupsRes.data) {
        signupsRes.data.forEach(c => {
          items.push({
            id: `signup-${c.id}`,
            type: 'recent_signup',
            icon: UserPlus,
            iconColor: '#22c55e',
            title: 'New signup',
            description: c.name,
            timestamp: c.created_at,
            action: () => onNavigate && onNavigate('company', { id: c.id, name: c.name })
          });
        });
      }

      // Commissions due
      if (commissionsRes.count === 0) {
        const monthLabel = now.toLocaleString('default', { month: 'long', year: 'numeric' });
        items.push({
          id: `commissions-${currentMonth}`,
          type: 'commissions_due',
          icon: Clock,
          iconColor: '#f7931e',
          title: 'Commissions not calculated',
          description: `${monthLabel} commissions are pending`,
          timestamp: now.toISOString(),
          action: () => onNavigate && onNavigate('page', { id: 'commissions' })
        });
      }

      // Sort by timestamp descending
      items.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setNotifications(items);
    } catch (err) {
      console.error('Notification fetch error:', err);
    }
  }, [onNavigate]);

  // Fetch on mount and every 5 minutes
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const unreadCount = notifications.filter(n => new Date(n.timestamp) > new Date(lastChecked)).length;

  const markAllRead = () => {
    const now = new Date().toISOString();
    setLastChecked(now);
    localStorage.setItem('wv_notif_last_checked', now);
  };

  const handleBellClick = () => {
    setShowDropdown(prev => !prev);
  };

  const handleNotificationClick = (notif) => {
    setShowDropdown(false);
    if (notif.action) notif.action();
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const t = new Date(timestamp);
    const diffMs = now - t;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return t.toLocaleDateString();
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        onClick={handleBellClick}
        style={{
          background: 'transparent',
          border: 'none',
          color: showDropdown ? '#ff6b35' : '#888',
          cursor: 'pointer',
          padding: '6px',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onMouseEnter={e => { if (!showDropdown) e.currentTarget.style.color = '#e0e0e0'; }}
        onMouseLeave={e => { if (!showDropdown) e.currentTarget.style.color = '#888'; }}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '0px',
            right: '0px',
            background: '#ef4444',
            color: '#fff',
            fontSize: '0.65rem',
            fontWeight: '700',
            minWidth: '16px',
            height: '16px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 4px',
            lineHeight: 1
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          width: '340px',
          background: '#1a1a1a',
          border: '1px solid #2a2a2a',
          borderRadius: '10px',
          zIndex: 200,
          boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            padding: '0.75rem 1rem',
            borderBottom: '1px solid #2a2a2a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ color: '#e0e0e0', fontWeight: '600', fontSize: '0.9rem' }}>
              Notifications
            </span>
            {unreadCount > 0 && (
              <span style={{
                background: '#ff6b3520',
                color: '#ff6b35',
                fontSize: '0.7rem',
                fontWeight: '600',
                padding: '2px 8px',
                borderRadius: '10px'
              }}>
                {unreadCount} new
              </span>
            )}
          </div>

          {/* Notification list */}
          <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{
                padding: '2rem 1rem',
                textAlign: 'center',
                color: '#666',
                fontSize: '0.85rem'
              }}>
                No notifications
              </div>
            ) : (
              notifications.map(notif => {
                const Icon = notif.icon;
                const isUnread = new Date(notif.timestamp) > new Date(lastChecked);
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      padding: '0.75rem 1rem',
                      background: isUnread ? '#1f1f1f' : 'transparent',
                      border: 'none',
                      borderBottom: '1px solid #222',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#2a2a2a'}
                    onMouseLeave={e => e.currentTarget.style.background = isUnread ? '#1f1f1f' : 'transparent'}
                  >
                    <div style={{
                      width: '32px',
                      height: '32px',
                      minWidth: '32px',
                      borderRadius: '8px',
                      background: `${notif.iconColor}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: '2px'
                    }}>
                      <Icon size={16} color={notif.iconColor} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '2px'
                      }}>
                        <span style={{
                          color: isUnread ? '#e0e0e0' : '#999',
                          fontSize: '0.85rem',
                          fontWeight: isUnread ? '600' : '400'
                        }}>
                          {notif.title}
                        </span>
                        {isUnread && (
                          <span style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: '#ff6b35',
                            flexShrink: 0
                          }} />
                        )}
                      </div>
                      <div style={{
                        color: '#888',
                        fontSize: '0.8rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {notif.description}
                      </div>
                      <div style={{
                        color: '#666',
                        fontSize: '0.7rem',
                        marginTop: '2px'
                      }}>
                        {formatTimeAgo(notif.timestamp)}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div style={{
              padding: '0.6rem 1rem',
              borderTop: '1px solid #2a2a2a',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <button
                onClick={markAllRead}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#ff6b35',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
              >
                Mark all as read
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Pages a recruiter is allowed to see in the sidebar
const RECRUITER_PAGES = new Set(['recruitment', 'messages']);

export default function Layout({ children, currentPage, setCurrentPage, userEmail, userRole, actualRole, previewAsRecruiter, onTogglePreviewRole, onLogout, onSearchNavigate }) {
  // For recruiters, collapse the sidebar to a single "Recruitment" section
  // containing only the pages they're allowed to use.
  const visibleSections = userRole === 'recruiter'
    ? [{
        id: 'recruitment-only',
        label: 'Recruitment',
        defaultOpen: true,
        items: sections
          .flatMap(s => s.items)
          .filter(item => RECRUITER_PAGES.has(item.id)),
      }]
    : sections;
  const [openSections, setOpenSections] = useState(() => {
    const initial = {};
    visibleSections.forEach(s => {
      // Open by default OR if it contains the active page
      initial[s.id] = s.defaultOpen || s.items.some(item => item.id === currentPage);
    });
    return initial;
  });

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  // Also expand section when navigating to a page within it
  const handleNavClick = (pageId) => {
    const parentSection = visibleSections.find(s => s.items.some(item => item.id === pageId));
    if (parentSection && !openSections[parentSection.id]) {
      setOpenSections(prev => ({ ...prev, [parentSection.id]: true }));
    }
    setCurrentPage(pageId);
    if (isMobile) setSidebarOpen(false);
  };

  // Handle notification navigation
  const handleNotificationNavigate = useCallback((type, item) => {
    if (type === 'company' && onSearchNavigate) {
      onSearchNavigate('company', item);
    } else if (type === 'page') {
      setCurrentPage(item.id);
    }
  }, [onSearchNavigate, setCurrentPage]);

  // company-viewer should highlight the companies nav item
  const activePage = currentPage === 'company-viewer' ? 'companies' : currentPage;

  const sidebarWidth = 240;

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      background: '#0a0a0a',
      overflow: 'hidden',
      maxWidth: '100vw'
    }}>
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 40
          }}
        />
      )}

      {/* Sidebar */}
      <div style={{
        width: `${sidebarWidth}px`,
        minWidth: `${sidebarWidth}px`,
        background: '#1a1a1a',
        borderRight: '1px solid #2a2a2a',
        padding: '1.5rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        left: isMobile && !sidebarOpen ? `-${sidebarWidth}px` : '0',
        top: 0,
        bottom: 0,
        zIndex: 50,
        transition: 'left 0.2s ease'
      }}>
        {/* Close button on mobile */}
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '0.5rem',
              background: 'transparent',
              border: 'none',
              color: '#888',
              cursor: 'pointer'
            }}
          >
            <X size={20} />
          </button>
        )}

        {/* Logo + Notification Bell */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '2rem',
          padding: '0 0.5rem'
        }}>
          <div style={{
            width: '36px',
            height: '36px',
            background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <LayoutDashboard size={20} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '1rem',
              fontWeight: '700',
              color: '#e0e0e0'
            }}>
              Work Van
            </div>
            <div style={{
              fontSize: '0.7rem',
              color: '#888',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Admin Portal
            </div>
          </div>
          <NotificationCenter onNavigate={handleNotificationNavigate} />
        </div>

        {/* Search */}
        <div style={{ padding: '0 0.25rem', marginBottom: '1rem' }}>
          {userRole === 'admin' && <GlobalSearch onSearchNavigate={onSearchNavigate} isMobile={false} />}
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, overflowY: 'auto' }}>
          {visibleSections.map(section => {
            const isOpen = openSections[section.id];
            const hasActivePage = section.items.some(item => item.id === activePage);
            const Chevron = isOpen ? ChevronDown : ChevronRight;

            return (
              <div key={section.id} style={{ marginBottom: '0.5rem' }}>
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.5rem 0.75rem',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    color: hasActivePage ? '#e0e0e0' : '#666',
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    textAlign: 'left'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#e0e0e0';
                  }}
                  onMouseLeave={(e) => {
                    if (!hasActivePage) {
                      e.currentTarget.style.color = '#666';
                    }
                  }}
                >
                  {section.label}
                  <Chevron size={14} />
                </button>

                {/* Section Items */}
                {isOpen && (
                  <div style={{ marginTop: '0.125rem' }}>
                    {section.items.map(item => {
                      const Icon = item.icon;
                      const isActive = activePage === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleNavClick(item.id)}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.625rem 1rem',
                            paddingLeft: '1.25rem',
                            background: isActive ? '#ff6b3520' : 'transparent',
                            border: 'none',
                            borderRadius: '8px',
                            color: isActive ? '#ff6b35' : '#888',
                            cursor: 'pointer',
                            marginBottom: '0.125rem',
                            transition: 'all 0.2s',
                            textAlign: 'left',
                            fontSize: '0.9rem'
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.background = '#2a2a2a';
                              e.currentTarget.style.color = '#e0e0e0';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.color = '#888';
                            }
                          }}
                        >
                          <Icon size={18} />
                          <span style={{ fontWeight: isActive ? '600' : '400' }}>
                            {item.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div style={{ borderTop: '1px solid #2a2a2a', paddingTop: '1rem', marginTop: '1rem' }}>
          {userEmail && (
            <div style={{
              padding: '0.5rem 1rem',
              marginBottom: '0.5rem',
              fontSize: '0.8rem',
              color: '#888'
            }}>
              {userEmail}
            </div>
          )}
          {/* View-as-recruiter — admin-only preview */}
          {actualRole === 'admin' && !previewAsRecruiter && (
            <button
              onClick={() => onTogglePreviewRole(true)}
              title="See exactly what Tara sees in the portal"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
                padding: '0.65rem 1rem',
                margin: '0 0 0.5rem',
                background: 'transparent',
                border: '1px solid #2a2a2a',
                borderRadius: '8px',
                color: '#888',
                cursor: 'pointer',
                width: 'calc(100% - 0px)',
                textAlign: 'left',
                fontSize: '0.85rem',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#1f1a14'; e.currentTarget.style.color = '#f0a500'; e.currentTarget.style.borderColor = '#f0a500'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#888'; e.currentTarget.style.borderColor = '#2a2a2a'; }}
            >
              <span style={{ fontSize: '1rem' }}>👁</span>
              <span>View as recruiter</span>
            </button>
          )}
          <button
            onClick={onLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.875rem 1rem',
              background: 'transparent',
              border: 'none',
              borderRadius: '8px',
              color: '#666',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#2a1a1a';
              e.currentTarget.style.color = '#e74c3c';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#666';
            }}
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        flex: 1,
        minHeight: '100vh',
        overflowX: 'auto',
        maxWidth: isMobile ? '100vw' : `calc(100vw - ${sidebarWidth}px)`
      }}>
        {/* Preview banner — visible only while an admin is viewing as recruiter */}
        {previewAsRecruiter && (
          <div style={{
            background: 'linear-gradient(135deg, #f0a500, #e69600)',
            color: '#231F20',
            padding: '0.5rem 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.75rem',
            fontSize: '0.85rem',
            fontWeight: 600,
            position: 'sticky',
            top: 0,
            zIndex: 40,
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          }}>
            <span>👁 Previewing as <strong>recruiter</strong> — this is what Tara will see</span>
            <button
              onClick={() => onTogglePreviewRole(false)}
              style={{
                background: '#231F20',
                color: '#f0a500',
                border: 'none',
                borderRadius: '6px',
                padding: '0.35rem 0.8rem',
                fontSize: '0.8rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Exit preview
            </button>
          </div>
        )}
        {/* Mobile header with hamburger */}
        {isMobile && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.75rem 1rem',
            background: '#1a1a1a',
            borderBottom: '1px solid #2a2a2a',
            position: 'sticky',
            top: 0,
            zIndex: 30
          }}>
            <button
              data-testid="hamburger"
              onClick={() => setSidebarOpen(true)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#e0e0e0',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <Menu size={24} />
            </button>
            <div style={{ flex: 1 }}>
              {userRole === 'admin' && <GlobalSearch onSearchNavigate={onSearchNavigate} isMobile={true} />}
            </div>
            <NotificationCenter onNavigate={handleNotificationNavigate} />
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
